/**
 * Co-Sign Domain
 * Tracks which buttons teammates are hovering: the hover that approves a team
 * decision. The server counts it for a majority vote — investing in a city,
 * placing the Wonder, whose approvers each pay an equal share of its price —
 * and for Knowledge Point co-pay. An ordinary cash purchase charges only the
 * buyer while cash pooling is switched off (ai/diagrams/budget-cosign-system.md).
 *
 * Button ids are built in constants/server-decisions.ts, as the server builds them.
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { OpCode, MessageType } from '../constants/op-codes';
import { cityInvestButtonId, wonderButtonId } from '../constants/server-decisions';
import { getViewerUserId } from './current-team';
import { teamPlayersOnline } from './players';
import { matchConnection, sendToMatch } from '../services/nakama/match-send';

// ==================== A CITY'S DECISIONS ====================

/** Whether a player is on one of a city's team decisions now — hovering or focusing its button. */
export function approvingAtCity(hoveredButtons: Record<string, string[]>, cityId: number, userId: string): boolean {
  return [cityInvestButtonId(cityId), wonderButtonId(cityId)].some((id) => hoveredButtons[id]?.includes(userId) ?? false);
}

// ==================== STORE ====================

interface CosignState {
  /** Map of button_id → [user_ids currently hovering] */
  hoveredButtons: Record<string, string[]>;

  /** Update a player's hover state on a button */
  updateButtonHover: (userId: string, buttonId: string, hovering: boolean) => void;

  /** Replace the full hoverer list for a button (from server authoritative state) */
  setButtonHoverers: (buttonId: string, hoverers: string[]) => void;

  /** Remove all hover entries for a player (on leave/disconnect) */
  removePlayer: (userId: string) => void;

  /** Every hover gone, as a session starts: in the full reset */
  reset: () => void;
}

const initialState = {
  hoveredButtons: {} as Record<string, string[]>,
};

export const useCosignStore = create<CosignState>()(
  devtools(
    (set) => ({
      ...initialState,

      updateButtonHover: (userId, buttonId, hovering) => {
        set(
          (state) => {
            const updated = { ...state.hoveredButtons };

            if (hovering) {
              // Add user to button's hoverer list
              const current = updated[buttonId] || [];
              if (!current.includes(userId)) {
                updated[buttonId] = [...current, userId];
              }
              // Remove user from any other button they were hovering
              for (const [bid, users] of Object.entries(updated)) {
                if (bid !== buttonId && users.includes(userId)) {
                  updated[bid] = users.filter((uid) => uid !== userId);
                  if (updated[bid].length === 0) {
                    delete updated[bid];
                  }
                }
              }
            } else {
              // Remove user from button's hoverer list
              if (updated[buttonId]) {
                updated[buttonId] = updated[buttonId].filter((uid) => uid !== userId);
                if (updated[buttonId].length === 0) {
                  delete updated[buttonId];
                }
              }
            }

            return { hoveredButtons: updated };
          },
          false,
          'updateButtonHover'
        );
      },

      setButtonHoverers: (buttonId, hoverers) => {
        set(
          (state) => {
            const updated = { ...state.hoveredButtons };
            if (hoverers.length === 0) {
              delete updated[buttonId];
            } else {
              updated[buttonId] = hoverers;
            }
            return { hoveredButtons: updated };
          },
          false,
          'setButtonHoverers'
        );
      },

      removePlayer: (userId) => {
        set(
          (state) => {
            const updated = { ...state.hoveredButtons };
            const removedFrom: string[] = [];
            for (const [bid, users] of Object.entries(updated)) {
              if (users.includes(userId)) {
                removedFrom.push(bid);
                updated[bid] = users.filter((uid) => uid !== userId);
                if (updated[bid].length === 0) {
                  delete updated[bid];
                }
              }
            }
            if (removedFrom.length > 0) {
              console.log('[CoSign] removePlayer', userId.slice(0, 8), 'from buttons:', removedFrom);
            }
            return { hoveredButtons: updated };
          },
          false,
          'removePlayer'
        );
      },

      reset: () => {
        console.log('[CoSign] reset — every hover gone');
        set(initialState, false, 'reset');
      },
    }),
    { name: 'cosign-store' }
  )
);

// ==================== SELECTORS ====================

/** A player as the approval count needs one: who, and on which team. */
interface Voter {
  user_id: string;
  cohort_id?: string;
}

/**
 * How many teammates a team decision needs: a majority of the team's players
 * online, as the server counts them (cosign.go: OnlineTeamMemberCount,
 * RequiredMajorityCount) — a teammate who has dropped counted out
 * (teamPlayersOnline), or the client would ask for a vote the server no
 * longer needs.
 */
export function requiredApprovals(
  players: Record<string, Voter>,
  disconnected: ReadonlySet<string>,
  cohortId: string | null | undefined,
): number {
  const online = teamPlayersOnline(players, disconnected, cohortId).length;
  return Math.floor(Math.max(online, 1) / 2) + 1;
}

// ==================== HANDLERS ====================

/**
 * Handle a teammate's button hover update from the server.
 */
export function handleButtonHoverUpdate(data: {
  type: string;
  user_id: string;
  button_id: string;
  hovering: boolean;
}) {
  console.log('[CoSign] Received hover UPDATE from server:', data.user_id.slice(0, 8), data.button_id, data.hovering ? 'ENTER' : 'LEAVE');
  useCosignStore.getState().updateButtonHover(data.user_id, data.button_id, data.hovering);
}

/**
 * Handle the full hoverer list for a button from the server.
 * Sent back to the sender on hover-enter so they know all current co-signers,
 * not just themselves (which is all the optimistic update provides).
 */
export function handleButtonHoverState(data: {
  type: string;
  button_id: string;
  hoverers: string[];
}) {
  console.log('[CoSign] Received hover STATE from server:', data.button_id, 'hoverers:', data.hoverers.map((h) => h.slice(0, 8)));
  const myId = getViewerUserId();
  let hoverers = data.hoverers;
  // A hover-enter snapshot can arrive after we've already left locally (RPC completion order).
  // Trust local "not hovering" over a stale list that still includes us.
  if (myId) {
    const localList = useCosignStore.getState().hoveredButtons[data.button_id] ?? [];
    if (!localList.includes(myId) && hoverers.includes(myId)) {
      hoverers = hoverers.filter((id) => id !== myId);
    }
  }
  useCosignStore.getState().setButtonHoverers(data.button_id, hoverers);
}

/**
 * A teammate's hovers go: they left the match (players.ts), or moved to
 * another section, where a hover of theirs is stale (section-presence.ts).
 */
export function handleTeammateHoversGone(userId: string): void {
  useCosignStore.getState().removePlayer(userId);
}

// ==================== SERVICE METHODS ====================

/**
 * Send a button hover event to the server and optimistically update the local store.
 * Called when the player's cursor enters or leaves a co-sign purchase button.
 *
 * Updates are applied **before** awaiting the socket send so rapid enter/leave matches UI intent;
 * otherwise RPC completions can reorder (leave finishes before enter) and the last write wins,
 * leaving the client visually stuck hovering.
 */
export async function sendButtonHover(buttonId: string, hovering: boolean): Promise<void> {
  if (!matchConnection()) {
    return;
  }

  const userId = getViewerUserId();
  if (!userId) {
    return;
  }

  const message = {
    type: MessageType.MessageTypeButtonHover,
    button_id: buttonId,
    hovering,
  };

  useCosignStore.getState().updateButtonHover(userId, buttonId, hovering);

  console.log('[CoSign] Sending hover:', buttonId, hovering ? 'ENTER' : 'LEAVE', 'user:', userId.slice(0, 8));
  if (!(await sendToMatch(OpCode.OpCodeButtonHover, message, 'a button hover'))) {
    // Revert optimistic update so UI matches server (still on prior hover state).
    useCosignStore.getState().updateButtonHover(userId, buttonId, !hovering);
  }
}
